import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Backdrop,
  IconButton
} from '@mui/material';
import { apiClient } from '../../api/apiClient';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import DeleteIcon from '@mui/icons-material/Delete';

const KATEGORIEN = [
  { code: 'R', name: 'Entscheidungen' },
  { code: 'G', name: 'Geschäftsverteilung' },
  { code: 'O', name: 'Organisationsuntersuchung - Extranet' },
  { code: 'V', name: 'Verwaltung' },
  { code: 'S', name: 'Statistik - Extranet' },
  { code: 'E', name: 'Rechtsgrundlagen - Extranet' },
  { code: 'B', name: 'Öffentliche Bekanntmachung gem. § 75 Abs. 2a SGG' },
  { code: 'P', name: 'Pressemitteilungen' }
];

export default function MitteilungenList() {
  const navigate = useNavigate();
  const [mitteilungen, setMitteilungen] = useState([]);
  const [gerichte, setGerichte] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedGericht, setSelectedGericht] = useState('');
  const [selectedKategorie, setSelectedKategorie] = useState('');
  const [textFilter, setTextFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Initialisierung
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const gerichteResponse = await apiClient.get('/gerichte/', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setGerichte(gerichteResponse.data);
      } catch (error) {
        console.error('Fehler beim Laden der Gerichte:', error);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  // Lade Mitteilungen wenn sich Filter ändern
  const fetchMitteilungen = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        size: rowsPerPage.toString()
      });

      if (textFilter) {
        params.append('text', textFilter);
      }

      if (selectedGericht) {
        params.append('gericht_id', selectedGericht);
      }

      if (selectedKategorie) {
        params.append('kategorie', selectedKategorie);
      }

      const response = await apiClient.get(`/mitteilungen/?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setMitteilungen(response.data.items);
      setTotalCount(response.data.total);
    } catch (error) {
      console.error('Fehler beim Laden der Mitteilungen:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, selectedGericht, selectedKategorie, textFilter]);

  useEffect(() => {
    fetchMitteilungen();
  }, [fetchMitteilungen]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
  };

  const handleGerichtChange = (event) => {
    setSelectedGericht(event.target.value);
    setPage(0);
  };

  const handleKategorieChange = (event) => {
    setSelectedKategorie(event.target.value);
    setPage(0);
  };

  const handleTextFilterChange = (event) => {
    setTextFilter(event.target.value);
    setPage(0);
  };

  const handleEdit = (mitteilung) => {
    navigate(`/admin/mitteilungen/${mitteilung.id}/edit`);
  };

  const handleAddNew = () => {
    navigate('/admin/mitteilungen/add');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie diese Mitteilung wirklich löschen?')) {
      try {
        await apiClient.delete(`/mitteilungen/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        fetchMitteilungen();
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
      }
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Mitteilungen verwalten
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Gericht</InputLabel>
          <Select
            value={selectedGericht}
            onChange={handleGerichtChange}
            label="Gericht"
          >
            <MenuItem value="">Alle</MenuItem>
            {gerichte.map((gericht) => (
              <MenuItem key={gericht.id} value={gericht.id}>
                {gericht.bezeichnung}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Kategorie</InputLabel>
          <Select
            value={selectedKategorie}
            onChange={handleKategorieChange}
            label="Kategorie"
          >
            <MenuItem value="">Alle</MenuItem>
            {KATEGORIEN.map((kategorie) => (
              <MenuItem key={kategorie.code} value={kategorie.code}>
                {kategorie.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Textsuche"
          value={textFilter}
          onChange={handleTextFilterChange}
          sx={{ minWidth: 200 }}
        />

        <Button
          variant="contained"
          onClick={handleAddNew}
          sx={{ height: 56 }}
        >
          Neue Mitteilung
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>Kategorie</TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>Gericht</TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>Titel</TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>Von</TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>Bis</TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mitteilungen.map((mitteilung) => (
              <TableRow 
                key={mitteilung.id}
                hover
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { 
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <TableCell 
                  sx={{ padding: '6px 6px 6px 16px' }}
                  onClick={() => handleEdit(mitteilung)}
                >
                  {KATEGORIEN.find(k => k.code === mitteilung.kategorie)?.name || mitteilung.kategorie}
                </TableCell>
                <TableCell 
                  sx={{ padding: '6px 6px 6px 16px' }}
                  onClick={() => handleEdit(mitteilung)}
                >
                  {mitteilung.gericht?.bezeichnung || '-'}
                </TableCell>
                <TableCell 
                  sx={{ padding: '6px 6px 6px 16px' }}
                  onClick={() => handleEdit(mitteilung)}
                >
                  {mitteilung.text || '-'}
                </TableCell>
                <TableCell 
                  sx={{ padding: '6px 6px 6px 16px' }}
                  onClick={() => handleEdit(mitteilung)}
                >
                  {format(new Date(mitteilung.von_datum), 'dd.MM.yyyy', { locale: de })}
                </TableCell>
                <TableCell 
                  sx={{ padding: '6px 6px 6px 16px' }}
                  onClick={() => handleEdit(mitteilung)}
                >
                  {format(new Date(mitteilung.bis_datum), 'dd.MM.yyyy', { locale: de })}
                </TableCell>
                <TableCell sx={{ padding: '6px 6px 6px 16px' }}>
                  <Button 
                    onClick={() => handleEdit(mitteilung)} 
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    Bearbeiten
                  </Button>
                  <IconButton 
                    onClick={() => handleDelete(mitteilung.id)} 
                    size="small" 
                    color="error"
                    aria-label="Löschen"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Zeilen pro Seite:"
      />

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
} 